import * as Sentry from "@sentry/react";
import {
  PracticeFormAnswer,
  PracticeTherapyDepartment,
  PracticeTreatmentContractType,
  SignPatientDocumentRequest,
  SignPatientDocumentResponse,
} from "@physiofit-digital/physiofit_types";
import { httpsCallable } from "firebase/functions";
import {
  getCurrentPracticePatientCostBearerDetails,
  getCurrentPracticeTreatmentContracts,
  getPracticePatient,
} from "../../../core/store/currentFormSlice";
import { useMainStore } from "../../../core/store/mainStore";
import { functions } from "../../../firebaseConfig";

export const useContractHandling = () => {
  const practicePatient = useMainStore(getPracticePatient);
  const setUserAnswerToQuestion = useMainStore(
    (state) => state.setUserAnswerToQuestion,
  );
  const currentPatientCostBearerDetails = useMainStore(
    getCurrentPracticePatientCostBearerDetails,
  );
  const currentPracticeTreatmentContracts = useMainStore(
    getCurrentPracticeTreatmentContracts,
  );

  // Both handlers return false instead of resolving silently, so the caller can
  // keep the patient on the question. A swallowed failure here used to carry
  // them to the success screen with the contract never signed (PF-5637).
  const handleSignNormalContract = async (
    contractToSign: PracticeFormAnswer["contractToSign"],
  ): Promise<boolean> => {
    if (!contractToSign) {
      console.error("Contract details are missing");
      Sentry.captureException(
        new Error("handleSignNormalContract: contract details are missing"),
      );
      return false;
    }

    const signPatientDocument = httpsCallable<
      SignPatientDocumentRequest,
      SignPatientDocumentResponse
    >(functions, "signPatientDocument");

    const patientId = practicePatient?.id;
    if (!patientId) {
      console.error("Patient ID is missing");
      Sentry.captureException(
        new Error("handleSignNormalContract: practicePatient id is missing"),
      );
      return false;
    }

    const storagePath = `practiceContracts/${contractToSign.documentId}`;

    try {
      const request: SignPatientDocumentRequest = {
        patientId: patientId,
        documentName: contractToSign.displayName,
        originalDocumentStoragePath: storagePath,
        deviceInfo: {
          userAgent: navigator.userAgent,
        },
        documentType: "other",
      };
      await signPatientDocument(request);
      return true;
    } catch (error) {
      console.error("Error signing contract:", error);
      Sentry.captureException(error);
      return false;
    }
  };

  const handleSignCostBearerContract = async (
    answerId: string,
    questionId: string,
  ): Promise<boolean> => {
    if (
      !currentPatientCostBearerDetails?.costBearerId ||
      !currentPatientCostBearerDetails?.departmentId
    ) {
      console.error("Department, Cost Bearer, or Contract details are missing");
      Sentry.captureException(
        new Error(
          "handleSignCostBearerContract: cost bearer details are missing",
        ),
      );
      return false;
    }

    const contractFileDetails =
      currentPracticeTreatmentContracts?.practiceTreatmentContracts?.[
        currentPatientCostBearerDetails.departmentId as PracticeTherapyDepartment
      ]?.contracts?.[
        currentPatientCostBearerDetails.costBearerId as PracticeTreatmentContractType
      ];

    if (!contractFileDetails) {
      console.error("Contract file details are missing");
      Sentry.captureException(
        new Error(
          "handleSignCostBearerContract: contract file details are missing",
        ),
      );
      return false;
    }

    const patientAnswer = {
      id: answerId,
      costBearerInfo: currentPatientCostBearerDetails,
    };

    setUserAnswerToQuestion(questionId, patientAnswer, answerId);

    const signPatientDocument = httpsCallable<
      SignPatientDocumentRequest,
      SignPatientDocumentResponse
    >(functions, "signPatientDocument");

    const patientId = practicePatient?.id;
    if (!patientId) {
      console.error("Patient ID is missing");
      Sentry.captureException(
        new Error("handleSignCostBearerContract: practicePatient id is missing"),
      );
      return false;
    }

    const storagePath = contractFileDetails.mergedStoragePath;

    try {
      const request: SignPatientDocumentRequest = {
        patientId: patientId,
        documentName: contractFileDetails.contractName,
        originalDocumentStoragePath: storagePath,
        deviceInfo: {
          userAgent: navigator.userAgent,
        },
        documentType: "treatmentContract",
        costBearerInfo: {
          departmentId: currentPatientCostBearerDetails.departmentId,
          costBearerId: currentPatientCostBearerDetails.costBearerId,
        },
      };
      await signPatientDocument(request);
      return true;
    } catch (error) {
      console.error("Error signing contract or updating patient data:", error);
      Sentry.captureException(error);
      return false;
    }
  };

  return {
    handleSignNormalContract,
    handleSignCostBearerContract,
  };
};
